import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLoaderData, useLocation, useParams } from 'react-router-dom';

import { LessonLoaderProps } from 'src/pages/LessonPage/loader';
import { TOP_NAVBAR_HEIGHT } from 'src/pages/LessonPage/children/Sidebar';

import { cleanupObserver } from './useIntersectionObserverForProgressTracking';

interface UseIntersectionObserverForSectionHighlightReturnType {
  selectedSectionId: string | undefined;
  clearSelectedSectionId: () => void;
}

export const useIntersectionObserverForSectionHighlight = (): UseIntersectionObserverForSectionHighlightReturnType => {
  const { course } = useLoaderData() as LessonLoaderProps;
  const location = useLocation();
  const params = useParams();

  const lessonSlug = params.lessonId;
  const [selectedSectionId, setSelectedSectionId] = useState<string>();
  const [visibleSections, setVisibleSections] = useState<Map<string, number>>(new Map());

  const sectionIds = useMemo(
    () =>
      course?.lessonsCollection.items.flatMap((lesson) =>
        lesson.sectionListCollection.items.map((section) => section.sys.id),
      ),
    [course],
  );

  const clearSelectedSectionId = useCallback(() => {
    setSelectedSectionId(undefined);
  }, []);

  useEffect(() => {
    let maxRatio = 0;
    let maxSectionId: string | undefined;

    visibleSections.forEach((ratio, sectionId) => {
      if (ratio > maxRatio) {
        maxRatio = ratio;
        maxSectionId = sectionId;
      }
    });

    if (maxSectionId) {
      setSelectedSectionId(maxSectionId);
    }
  }, [visibleSections]);

  useEffect(() => {
    const seenSectionObserver: IntersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const currentSectionId = entry.target.id;

          setVisibleSections((prev) => {
            const newMap = new Map(prev);

            if (entry.isIntersecting) {
              newMap.set(currentSectionId, entry.intersectionRatio);
            } else {
              newMap.delete(currentSectionId);
            }

            return newMap;
          });
        });
      },
      {
        threshold: [0, 0.25, 0.5, 0.75, 1],
        rootMargin: `${TOP_NAVBAR_HEIGHT}px 0px 0px 0px`,
      },
    );

    if (!sectionIds) {
      return;
    }

    for (const section of [...sectionIds, 'quiz']) {
      const element = document.getElementById(section);

      if (element) {
        seenSectionObserver.observe(element);
      }
    }

    return () => cleanupObserver(sectionIds, seenSectionObserver);
  }, [sectionIds, lessonSlug, location.hash]);

  return { selectedSectionId, clearSelectedSectionId };
};
